import { mergeAttributes, Node } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'

import { POLL_DATA_TYPE } from './constants.js'
import { PollNodeContent } from './PollNodeContent.js'

declare module '@tiptap/core' {
  interface EditorOptions {
    poll?:
      | {
          state: 'creating'
        }
      | {
          state: 'updating'
          id: string
        }
  }

  interface Commands<ReturnType> {
    pollNode: {
      createPoll: () => ReturnType
      editPoll: ({ id }: { id: string }) => ReturnType
      insertPoll: ({ id }: { id: string }) => ReturnType
      closePoll: () => ReturnType
      deletePoll: ({ id }: { id: string }) => ReturnType
    }
  }
}

export const PollNode = Node.create({
  name: 'pollNode',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  inline: false,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: element => {
          return element.getAttribute('data-poll-id')
        },
        renderHTML: attrs => ({
          'data-poll-id': attrs.id,
        }),
      },
      updatedAt: {
        default: null,
        rendered: false,
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div',
        getAttrs: element => {
          return element.getAttribute('data-type') === POLL_DATA_TYPE && null
          /*  
          ProseMirror expects null or undefined if the check is successful.
          https://prosemirror.net/docs/ref/version/0.18.0.html#model.ParseRule.getAttrs
          */
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-type': POLL_DATA_TYPE }),
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(PollNodeContent, {
      attrs: ({ node }) => ({
        'data-poll-id': node.attrs.id,
      }),
    })
  },

  addCommands() {
    return {
      createPoll:
        () =>
        ({ editor }) => {
          editor.setOptions({
            poll: {
              state: 'creating',
            },
          })
          return true
        },
      insertPoll:
        attrs =>
        ({ editor, chain, tr, dispatch }) => {
          editor.setOptions({
            poll: undefined,
          })

          // we're doing this because we want to force re-render the poll node
          const nodes = editor.$nodes(PollNode.name, attrs)
          if (nodes.length > 0) {
            nodes.forEach(node => {
              tr.setNodeMarkup(node.from, undefined, {
                ...node.attributes,
                updatedAt: new Date().getTime(),
              })
            })
            return dispatch(tr)
          }

          if (editor.storage.poll) {
            editor.storage.poll.createdPollIds.push(attrs.id)
          }

          return chain()
            .insertContent(
              {
                type: this.name,
                attrs,
              },
              { updateSelection: true },
            )
            .run()
        },
      editPoll:
        ({ id }: { id: string }) =>
        ({ editor }) => {
          editor.setOptions({
            poll: { state: 'updating', id },
          })
          return true
        },

      closePoll:
        () =>
        ({ editor }) => {
          editor.setOptions({
            poll: undefined,
          })
          return true
        },
    }
  },
})
